// Real in-app notifications client (notification-service, Go, :3003).
// Auth is the httpOnly session cookie + CSRF (see lib/session.ts), same as hrApi.
// The SSE stream is authenticated by the gateway via the httpOnly cookie, so the
// EventSource is opened with { withCredentials: true } and no token in the URL —
// see subscribeNotifications below.
import axios, { AxiosInstance } from 'axios';
import { refreshAccessToken } from './authRefresh';
import { attachCookieAuth } from '@/lib/session';
import { apiConfig } from '@/config/api.config';

const NOTIFICATION_URL =
  import.meta.env.VITE_NOTIFICATION_URL || 'http://localhost:3003';

function withAuth(baseURL: string): AxiosInstance {
  const instance = axios.create({ baseURL, timeout: 15000, withCredentials: true });
  instance.interceptors.request.use(attachCookieAuth);
  return instance;
}

const api = withAuth(NOTIFICATION_URL);

// Silent refresh-and-retry on auth failure (mirrors hrApi).
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    const original = err?.config;
    if ((status === 401 || status === 403) && original && !original._retried) {
      original._retried = true;
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        // refreshAccessToken now rotates the httpOnly cookie; a truthy result
        // means "retry" — the cookie carries the new credential, no Bearer.
        return api(original);
      }
    }
    return Promise.reject(err);
  },
);

export interface AppNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  link?: string;
  entityType?: string;
  entityId?: string;
  actionable?: boolean;
  isRead: boolean;
  createdAt: string;
}

// resolveNotificationLink turns a stored notification link into a navigable
// in-app path: tenant-prefixes /app links, and rescues legacy rows that stored
// ABSOLUTE URLs (their pathname is extracted). Returns null when the link
// can't be resolved — callers fall back to the notifications page.
//
// `entity` (the row's entityType/entityId) lets a list-page link open the ONE
// record it is about: delegation notifications store the generic
// `/app/admin/delegations`, so the delegation id is added as `?id=` — the
// page's deep-link parameter — unless the link already names one.
export function resolveNotificationLink(
  link: string | undefined,
  tenantSlug?: string,
  entity?: { entityType?: string; entityId?: string },
): string | null {
  if (!link) return null;
  let path = link;
  if (/^https?:\/\//i.test(link)) {
    try { path = new URL(link).pathname + new URL(link).search; } catch { return null; }
  }
  if (!path.startsWith('/app')) return null;
  if (entity?.entityType === 'delegation' && entity.entityId && /^\/app\/admin\/delegations\/?(\?|$)/.test(path) && !/[?&]id=/.test(path)) {
    path += `${path.includes('?') ? '&' : '?'}id=${encodeURIComponent(entity.entityId)}`;
  }
  return tenantSlug ? `/t/${tenantSlug}${path}` : path;
}

export interface NotificationList {
  items: AppNotification[];
  unread: number;
}

export const listNotifications = () =>
  api.get<NotificationList>('/api/v1/notifications').then((r) => r.data);

export const getUnreadCount = () =>
  api.get<{ unread: number }>('/api/v1/notifications/unread-count').then((r) => r.data.unread);

export const markNotificationRead = (id: string) =>
  api.post(`/api/v1/notifications/${id}/read`).then((r) => r.data);

export const markAllNotificationsRead = () =>
  api.post('/api/v1/notifications/read-all').then((r) => r.data);

export interface CreateNotification {
  title: string;
  message?: string;
  type?: string;
  link?: string;
  userId?: string;
  email?: string;
}
export const createNotification = (data: CreateNotification) =>
  api.post('/api/v1/notifications', data).then((r) => r.data);

/**
 * Subscribe to the live notification stream (SSE). The gateway authenticates the
 * stream route via the httpOnly session cookie, so the EventSource is opened with
 * { withCredentials: true } and NO token in the URL. The stream URL points at the
 * gateway (apiConfig.baseURL) so the cookie is sent on the handshake. Returns an
 * unsubscribe function.
 */
export function subscribeNotifications(
  onUpdate: (data: NotificationList) => void,
): () => void {
  let es: EventSource | null = null;
  let closed = false;
  let attempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  // Point at the gateway (same origin as the httpOnly cookie) so credentials are
  // sent with the SSE handshake.
  const streamUrl = `${apiConfig.baseURL}/api/v1/notifications/stream`;

  const open = () => {
    if (closed) return;
    try {
      es = new EventSource(streamUrl, { withCredentials: true });
    } catch {
      scheduleReconnect();
      return;
    }
    es.onopen = () => {
      attempt = 0;
    };
    es.addEventListener('notifications', (ev: MessageEvent) => {
      attempt = 0; // a delivered frame means the connection is healthy
      try {
        onUpdate(JSON.parse(ev.data));
      } catch {
        /* ignore malformed frame */
      }
    });
    es.onerror = () => {
      // A dropped/expired stream still errors; close it (this also stops the
      // browser's native reconnect loop) and reconnect with backoff — the fresh
      // handshake re-sends the httpOnly cookie automatically.
      if (es) {
        es.close();
        es = null;
      }
      scheduleReconnect();
    };
  };

  const scheduleReconnect = () => {
    if (closed || reconnectTimer) return;
    const delay = Math.min(1000 * 2 ** attempt, 30000);
    attempt += 1;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      if (closed) return;
      open();
    }, delay);
  };

  open();

  return () => {
    closed = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    if (es) es.close();
  };
}
